import type { StatControllerListPlatformResponse } from 'api/stat/Stat.schema.ts';
import { dateToDM, dateToLLL } from 'common/date.ts';
import { GET, isSuperadmin, MPOST } from 'common/helpers.ts';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { Button } from 'ui/component/Button.tsx';
import { CopyableText } from 'ui/component/CopyableText.js';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tooltip as TooltipText } from 'ui/component/Tooltip.tsx';
import { useAlert, useConfirm, usePrompt } from '#admin/component/DialogProvider.tsx';
import { KPI } from '#admin/component/KPI.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

type ExportRow = { timestamp: string; api_key_label: string; count: number };

const CHART_COLORS = ['#1764ff', '#25d3ff', '#04994a', '#ff9c31', '#202bf9', '#c22a23', '#7deab3', '#f7ce46'];

export const Operator = () => {
  const { me } = useMe();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const alert = useAlert();

  React.useEffect(() => {
    if (!isSuperadmin(me)) navigate('/');
  }, [me, navigate]);

  const { data: stats, error: statsError } = useSWR<StatControllerListPlatformResponse>(me ? ['/stat/list-platform'] : null, GET, { refreshInterval: 60_000 });
  const { data: queries } = useSWR<any>(me ? ['/operator/queries'] : null, GET, { refreshInterval: 1_000 });

  const syncUsersMutation = useSWRMutation('/operator/sync-users', MPOST);
  const syncShipmentsMutation = useSWRMutation('/operator/sync-shipments', MPOST);
  const syncLegsMutation = useSWRMutation('/operator/sync-legs', MPOST);
  const syncDocsMutation = useSWRMutation('/operator/sync-docs', MPOST);
  const resetRolesPermissionsMutation = useSWRMutation('/operator/reset-roles-permissions', MPOST);
  const reassignShipmentsMutation = useSWRMutation('/operator/reassign-shipments', MPOST);
  const uncacheMutation = useSWRMutation('/operator/uncache', MPOST);
  const maintenanceMutation = useSWRMutation('/operator/maintenance', MPOST);
  const initMutation = useSWRMutation('/operator/init', MPOST);

  const confirmMutation = React.useCallback(
    (mutation: any, payload: any = {}) =>
      async () => {
        if (!(await confirm('Do you confirm the operation?'))) return;
        void mutation.trigger(payload);
        await alert('Operation scheduled, check the server logs for the result.');
      },
    [confirm, alert],
  );

  const promptMutation = React.useCallback(
    (mutation: any, payload: any = {}, valueDescription: string, key: string) =>
      async () => {
        const value = await prompt(`To confirm the operation, provide the ${valueDescription}`);
        void mutation.trigger({ ...payload, [key]: value || undefined });
        await alert('Operation scheduled, check the server logs for the result.');
      },
    [prompt, alert],
  );

  const exportRows = (stats?.shipments_export_history ?? []) as ExportRow[];
  const exportData = React.useMemo(
    () =>
      Object.values(
        exportRows.reduce<Record<string, Record<string, unknown>>>((acc, row) => {
          if (!acc[row.timestamp]) acc[row.timestamp] = { timestamp: row.timestamp };
          acc[row.timestamp][row.api_key_label] = Number(row.count);
          return acc;
        }, {}),
      ),
    [exportRows],
  );
  const exportLabels = React.useMemo(() => [...new Set(exportRows.map((r) => r.api_key_label))], [exportRows]);

  if (statsError) return <div>Error fetching KPIs: {statsError.message}</div>;
  if (!stats) return <Spinner centered={true} />;

  const { ACTIVE_USERS, TOTAL_USERS, ACTIVE_ADMINS, TOTAL_ADMINS, ACTIVE_AGENTS, TOTAL_AGENTS, ACTIVE_DRIVERS, TOTAL_DRIVERS } = stats;
  const { ACTIVE_SHIPMENTS, TOTAL_SHIPMENTS, ACTIVE_LEGS, TOTAL_LEGS, TRACKED_SHIPMENTS, DOCUMENTS } = stats;

  return (
    <main className="container m-auto flex w-full flex-col px-10">
      <div className="container mb-4 flex flex-col gap-8">
        <div className="lg grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
          <h1 className="col-span-full col-start-1 mt-6">Operator actions</h1>

          <div className="col-span-full flex flex-wrap gap-4">
            <Button className="grey w-max" onClick={confirmMutation(syncUsersMutation)} disabled={syncUsersMutation.isMutating} loading={syncUsersMutation.isMutating}>
              Sync users
            </Button>

            <Button className="grey w-max" onClick={confirmMutation(syncShipmentsMutation)} disabled={syncShipmentsMutation.isMutating} loading={syncShipmentsMutation.isMutating}>
              Sync shipments
            </Button>

            <Button className="grey w-max" onClick={confirmMutation(syncLegsMutation)} disabled={syncLegsMutation.isMutating} loading={syncLegsMutation.isMutating}>
              Sync legs
            </Button>

            <Button className="grey w-max" onClick={confirmMutation(syncDocsMutation)} disabled={syncDocsMutation.isMutating} loading={syncDocsMutation.isMutating}>
              Sync docs
            </Button>

            <Button
              className="grey w-max"
              onClick={promptMutation(reassignShipmentsMutation, {}, 'last shipment id reassigned (if any):', 'last_shipment_id')}
              disabled={reassignShipmentsMutation.isMutating}
              loading={reassignShipmentsMutation.isMutating}
            >
              REASSIGN SHIPMS
            </Button>

            <Button
              className="grey w-max"
              onClick={confirmMutation(resetRolesPermissionsMutation)}
              disabled={resetRolesPermissionsMutation.isMutating}
              loading={resetRolesPermissionsMutation.isMutating}
            >
              Reset roles
            </Button>

            <Button
              className="red w-max"
              onClick={confirmMutation(maintenanceMutation, { status: true })}
              disabled={maintenanceMutation.isMutating}
              loading={maintenanceMutation.isMutating}
            >
              Enable maintenance
            </Button>

            <Button className="red w-max" onClick={confirmMutation(initMutation)} disabled={initMutation.isMutating} loading={initMutation.isMutating}>
              Reinit
            </Button>

            <Button className="red w-max" onClick={confirmMutation(uncacheMutation)} disabled={uncacheMutation.isMutating} loading={uncacheMutation.isMutating}>
              Uncache
            </Button>
          </div>
        </div>

        <div className="lg grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
          <h1 className="col-span-full col-start-1 mt-6">Users stats</h1>
          <p className="col-span-full col-start-1 mb-3">The user is considered active if an action was performed in the last 2d.</p>
          <KPI className="col-start-1" title={'Users'} value={TOTAL_USERS} comparisonValue={`(${ACTIVE_USERS} active)`} />
          <KPI className="" title={'Admins'} value={TOTAL_ADMINS} comparisonValue={`(${ACTIVE_ADMINS} active)`} />
          <KPI className="" title={'Agents'} value={TOTAL_AGENTS} comparisonValue={`(${ACTIVE_AGENTS} active)`} />
          <KPI className="" title={'Drivers'} value={TOTAL_DRIVERS} comparisonValue={`(${ACTIVE_DRIVERS} active)`} />
        </div>

        <div className="lg grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
          <h1 className="col-start-1 mt-6">Shipments stats</h1>
          <KPI
            className="col-start-1"
            title={'Active shipments'}
            value={ACTIVE_SHIPMENTS}
            comparisonValue={`/ ${TOTAL_SHIPMENTS}`}
            description={'Count of shipments pending delivery.'}
          />

          <KPI className="" title={'Active legs'} value={ACTIVE_LEGS} comparisonValue={`/ ${TOTAL_LEGS}`} description={'Count of legs pending delivery.'} />

          <KPI className="" title={'Tracked shipments'} value={TRACKED_SHIPMENTS} comparisonValue={`/ ${ACTIVE_SHIPMENTS}`} />

          <KPI className="" title={'Documents'} value={DOCUMENTS} />

          <div className="paper !p-6 !pb-0 col-span-2 flex h-[200px] flex-col gap-4 border-2 border-blue-200">
            <h3 className="blue">Shipments imported in the last 7d</h3>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.shipments_import_history} margin={{ left: -10, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" tickFormatter={(value) => dateToDM(value)} />
                <YAxis dataKey="count" />
                <Bar dataKey="count" fill="#000">
                  <LabelList dataKey="count" position="top" formatter={(value) => value || ''} />
                </Bar>
                <Tooltip wrapperStyle={{ zIndex: 50 }} labelFormatter={(value) => dateToLLL(value)} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="paper !p-6 !pb-0 col-span-2 flex h-[200px] flex-col gap-4 border-2 border-blue-200">
            <h3 className="blue">Acknowledgments by integration (last 7d)</h3>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={exportData} margin={{ left: -10, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" tickFormatter={(value) => dateToDM(value)} />
                <YAxis />
                <Tooltip wrapperStyle={{ zIndex: 50 }} labelFormatter={(value) => dateToLLL(value)} />
                {exportLabels.map((label, i) => (
                  <Bar key={label} dataKey={label} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="paper !p-6 col-span-4 flex flex-col gap-4 border-2 border-blue-200">
            <h3 className="blue">Current queries</h3>
            {!queries && <Spinner centered={true} />}
            <div>
              {queries?.map((query) => (
                <div key={query.pid} className="grid grid-cols-[50px_auto_50px] gap-x-2 border-1 border-grey-border border-b-0 p-2 last:border-b-1">
                  <span>{query.pid}</span>
                  <TooltipText text={query.query} className="truncate" data-tooltip-delay-hide="1000">
                    <CopyableText copyText={query.query} tooltipText={query.query}>
                      <span>{query.query}</span>
                    </CopyableText>
                  </TooltipText>
                  <span>{query.time}s</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
